import { Link, useParams, useSearchParams } from "react-router";
import { Card, CardContent, CardHeader, CardTitle, DataTable, Tag, type ColumnDef } from "@diametral/design-system/react";

import { usePersonFollowUp } from "../api/followUp";
import type {
  ActivityReport,
  FollowUpAssignment,
  MembershipRequest,
  MissingReport,
  PersonFollowUp,
  YearAllocation,
} from "../api/types";
import { ReportStatusTag, RequestStatusTag } from "../components/Badges";
import { Page, useDocumentTitle } from "../components/Page";
import { PlainText } from "../components/PlainText";
import { SelectField } from "../components/SelectField";
import { EmptyBlock, ErrorBlock, LoadingBlock } from "../components/States";
import { ApiError } from "../lib/api";
import { formatDate, formatDateTime } from "../lib/format";
import { currentLocale, t, useTranslation } from "../lib/i18n";
import { CHANNEL, REQUEST_KIND } from "../lib/labels";
import { REPORT_FIELD_DEFAULTS } from "../lib/reference";
import { compareWeeks, isIsoWeek, todayIso, weekLabel } from "../lib/week";
import "../styles/follow-up.css";

/** The first year ada holds data for; the select offers it up to next year. */
const FIRST_YEAR = 2024;

function currentYear(): number {
  return Number(todayIso().slice(0, 4));
}

export function yearOptions(selected: number, now = currentYear()): number[] {
  const years: number[] = [];
  for (let y = now + 1; y >= Math.min(FIRST_YEAR, selected); y--) years.push(y);
  return years;
}

function readYear(value: string | null): number {
  const year = Number(value);
  const now = currentYear();
  return Number.isInteger(year) && year >= 2000 && year <= now + 1 ? year : now;
}

// ------------------------------------------------------------------------------ allocation

/**
 * Shade step of a cell from its share of the month's working days: 0 (none) to 4 (full month).
 * The steps map onto the DS sequential ramp `--ds-heat-*` in follow-up.css.
 */
export function shareLevel(days: number, working: number): 0 | 1 | 2 | 3 | 4 {
  if (days <= 0 || working <= 0) return 0;
  const share = days / working;
  if (share >= 1) return 4;
  if (share > 0.5) return 3;
  if (share > 0.25) return 2;
  return 1;
}

function monthLabels(months: string[]) {
  const locale = currentLocale();
  const short = new Intl.DateTimeFormat(locale, { month: "short", timeZone: "UTC" });
  const long = new Intl.DateTimeFormat(locale, { month: "long", year: "numeric", timeZone: "UTC" });
  return months.map((month) => {
    const [y, m] = month.split("-").map(Number);
    const date = new Date(Date.UTC(y ?? 2000, (m ?? 1) - 1, 1));
    return { month, short: short.format(date), long: long.format(date) };
  });
}

function percent(value: number): string {
  return new Intl.NumberFormat(currentLocale(), { style: "percent", maximumFractionDigits: 0 }).format(value);
}

function DaysCell({ days, working }: { days: number; working: number }) {
  const level = shareLevel(days, working);
  return (
    <td className="ada-alloc__cell" data-level={level} title={working ? percent(days / working) : undefined}>
      {days}
    </td>
  );
}

function AllocationTable({ allocation, year }: { allocation: YearAllocation; year: number }) {
  const { t } = useTranslation();
  const months = monthLabels(allocation.months);
  const thisMonth = todayIso().slice(0, 7);
  const legend = [
    { level: 1, label: `≤ ${percent(0.25)}` },
    { level: 2, label: `≤ ${percent(0.5)}` },
    { level: 3, label: `< ${percent(1)}` },
    { level: 4, label: percent(1) },
  ];
  return (
    <div className="ada-stack ada-stack--tight">
      <div className="ada-table-wrap">
        <table className="ada-alloc">
          <caption className="ada-alloc__caption">
            {t(
              "Working days assigned per project and month in {{year}}. The darker the cell, the larger the share of the month's working days; months to come (in italics) show the planned allocation.",
              { year },
            )}
          </caption>
          <thead>
            <tr>
              <th scope="col" className="ada-alloc__rowhead">
                {t("Project")}
              </th>
              {months.map((m) => (
                <th
                  key={m.month}
                  scope="col"
                  title={m.long}
                  className={m.month > thisMonth ? "ada-alloc__future" : undefined}
                >
                  <abbr title={m.long}>{m.short}</abbr>
                </th>
              ))}
              <th scope="col">{t("Total")}</th>
            </tr>
          </thead>
          <tbody>
            {allocation.projects.map((project) => (
              <tr key={project.project}>
                <th scope="row" className="ada-alloc__rowhead">
                  <Link className="ada-link" to={`/projects/${project.project}`}>
                    {project.project_title}
                  </Link>
                </th>
                {project.days.map((days, i) => (
                  <DaysCell key={allocation.months[i]} days={days} working={allocation.working_days[i] ?? 0} />
                ))}
                <td className="ada-alloc__total">{project.total_days}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <th scope="row" className="ada-alloc__rowhead">
                {t("Assigned / working days")}
              </th>
              {allocation.assigned_days.map((days, i) => {
                const working = allocation.working_days[i] ?? 0;
                return (
                  <td key={allocation.months[i]} className="ada-alloc__cell" data-level={shareLevel(days, working)}>
                    {days}
                    <span className="ada-alloc__of">/{working}</span>
                  </td>
                );
              })}
              <td className="ada-alloc__total">
                {allocation.total_assigned_days}
                <span className="ada-alloc__of">/{allocation.total_working_days}</span>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
      <p className="ada-alloc__legend ada-faint">
        <span>{t("Share of the month's working days:")}</span>
        {legend.map((item) => (
          <span key={item.level} className="ada-alloc__legend-item">
            <span className="ada-alloc__swatch" data-level={item.level} aria-hidden="true" />
            {item.label}
          </span>
        ))}
      </p>
    </div>
  );
}

function AllocationCard({ data }: { data: PersonFollowUp }) {
  const { t } = useTranslation();
  const { allocation, year } = data;
  const share = allocation.total_working_days ? allocation.total_assigned_days / allocation.total_working_days : 0;
  return (
    <Card>
      <CardHeader>
        <div className="ada-row">
          <CardTitle>{t("Allocation in {{year}}", { year })}</CardTitle>
          <span className="ada-faint ds-numeric">
            {t("{{assigned}} of {{working}} working days assigned ({{percent}})", {
              assigned: allocation.total_assigned_days,
              working: allocation.total_working_days,
              percent: percent(share),
            })}
          </span>
        </div>
      </CardHeader>
      <CardContent>
        {allocation.projects.length ? (
          <AllocationTable allocation={allocation} year={year} />
        ) : (
          <p className="ada-faint">{t("No assignment in {{year}}.", { year })}</p>
        )}
      </CardContent>
    </Card>
  );
}

// ----------------------------------------------------------------------------- assignments

function period(start: string, end: string | null): string {
  if (end) return `${formatDate(start)} – ${formatDate(end)}`;
  if (start > todayIso()) return t("from {{date}}", { date: formatDate(start) });
  return t("since {{date}}", { date: formatDate(start) });
}

function AssignmentsCard({ data }: { data: PersonFollowUp }) {
  const { t } = useTranslation();
  const columns: ColumnDef<FollowUpAssignment, unknown>[] = [
    {
      accessorKey: "project_title",
      header: t("Project"),
      cell: ({ row }) => (
        <Link className="ada-link" to={`/projects/${row.original.project}`}>
          {row.original.project_title}
        </Link>
      ),
    },
    { accessorKey: "stream", header: t("Stream") },
    {
      accessorKey: "start_date",
      header: t("Period"),
      cell: ({ row }) => <span className="ada-nowrap">{period(row.original.start_date, row.original.end_date)}</span>,
    },
    {
      accessorKey: "source",
      header: t("Origin"),
      cell: ({ row }) => (row.original.source === "request" ? t("On request") : t("By the DAR")),
    },
  ];
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("Assignments")}</CardTitle>
      </CardHeader>
      <CardContent>
        {data.assignments.length ? (
          <div className="ada-table-wrap">
            <DataTable columns={columns} data={data.assignments} rowKey={(a) => String(a.id)} />
          </div>
        ) : (
          <p className="ada-faint">{t("No assignment in {{year}}.", { year: data.year })}</p>
        )}
      </CardContent>
    </Card>
  );
}

// --------------------------------------------------------------------------------- reports

interface WeekGroup {
  week: string;
  reports: ActivityReport[];
  missing: MissingReport[];
}

/** Reports and missing sections merged by week, most recent week first. */
export function weeksOf(reports: ActivityReport[], missing: MissingReport[]): WeekGroup[] {
  const groups = new Map<string, WeekGroup>();
  const group = (week: string) => {
    const existing = groups.get(week);
    if (existing) return existing;
    const created: WeekGroup = { week, reports: [], missing: [] };
    groups.set(week, created);
    return created;
  };
  for (const report of reports) group(report.week).reports.push(report);
  for (const item of missing) group(item.week).missing.push(item);
  return [...groups.values()].sort((a, b) => compareWeeks(b.week, a.week));
}

function FollowUpReport({ report }: { report: ActivityReport }) {
  const { t } = useTranslation();
  return (
    <Card size="sm">
      <CardHeader>
        <div className="ada-row">
          <Link className="ada-link" to={`/projects/${report.project}`}>
            {report.project_title}
          </Link>
          <span className="ada-cluster">
            {report.absent ? <Tag tone="info">{t("Absent / no activity")}</Tag> : null}
            <ReportStatusTag status={report.status} />
            <span className="ada-faint">
              {report.source !== "ui" ? `${CHANNEL[report.source]} · ` : ""}
              {formatDateTime(report.submitted_at ?? report.updated_at)}
            </span>
          </span>
        </div>
      </CardHeader>
      <CardContent>
        <dl className="ada-report-fields">
          <div>
            <dt className="ds-label">{REPORT_FIELD_DEFAULTS.done.label}</dt>
            <dd>
              <PlainText text={report.done} empty={report.absent ? "—" : t("Not filled in")} />
            </dd>
          </div>
          <div>
            <dt className="ds-label">{REPORT_FIELD_DEFAULTS.next.label}</dt>
            <dd>
              <PlainText text={report.next} />
            </dd>
          </div>
          <div>
            <dt className="ds-label">{REPORT_FIELD_DEFAULTS.blockers.label}</dt>
            <dd>
              <PlainText text={report.blockers} />
            </dd>
          </div>
        </dl>
      </CardContent>
    </Card>
  );
}

function ReportHistory({ data }: { data: PersonFollowUp }) {
  const { t } = useTranslation();
  const weeks = weeksOf(data.reports, data.missing_reports);
  return (
    <section className="ada-stack" aria-labelledby="ada-fu-reports">
      <div className="ada-row">
        <h2 id="ada-fu-reports" className="ada-section-title">
          {t("Activity reports")}
        </h2>
        <span className="ada-faint ds-numeric">
          {t("{{count}} reports", { count: data.reports.length })}
          {data.missing_reports.length
            ? ` · ${t("{{count}} missing reports", { count: data.missing_reports.length })}`
            : ""}
        </span>
      </div>
      {weeks.length ? (
        <div className="ada-stack ada-stack--loose">
          {weeks.map((group) => {
            const label = isIsoWeek(group.week) ? weekLabel(group.week) : group.week;
            return (
              <section
                key={group.week}
                className={`ada-week ada-fu-week${group.missing.length ? " ada-fu-week--missing" : ""}`}
                aria-label={label}
              >
                <div className="ada-week__head">
                  <h3 className="ada-section-title">{label}</h3>
                  {group.missing.length ? <Tag tone="danger">{t("Missing")}</Tag> : null}
                </div>
                {group.missing.map((item) => (
                  <div key={item.project} className="ada-fu-missing">
                    <Link className="ada-link" to={`/projects/${item.project}`}>
                      {item.project_title}
                    </Link>
                    <span className="ada-cluster">
                      <span className="ada-faint">{t("No report filed for this week.")}</span>
                      <Tag tone="danger">{t("Missing")}</Tag>
                    </span>
                  </div>
                ))}
                {group.reports.map((report) => (
                  <FollowUpReport key={report.id} report={report} />
                ))}
              </section>
            );
          })}
        </div>
      ) : (
        <EmptyBlock icon="edit" title={t("No activity report in {{year}}", { year: data.year })} />
      )}
    </section>
  );
}

// -------------------------------------------------------------------------------- requests

function RequestsCard({ data }: { data: PersonFollowUp }) {
  const { t } = useTranslation();
  const columns: ColumnDef<MembershipRequest, unknown>[] = [
    {
      accessorKey: "created_at",
      header: t("Date"),
      cell: ({ row }) => <span className="ada-nowrap">{formatDateTime(row.original.created_at)}</span>,
    },
    { accessorKey: "kind", header: t("Request"), cell: ({ row }) => REQUEST_KIND[row.original.kind] },
    {
      accessorKey: "project_title",
      header: t("Project"),
      cell: ({ row }) => (
        <div className="ada-stack ada-stack--tight">
          <Link className="ada-link" to={`/projects/${row.original.project}`}>
            {row.original.project_title}
          </Link>
          <span className="ada-faint">
            {row.original.stream ? t("Stream “{{stream}}”", { stream: row.original.stream }) : t("Whole project")}
          </span>
        </div>
      ),
    },
    {
      accessorKey: "status",
      header: t("Status"),
      cell: ({ row }) => <RequestStatusTag status={row.original.status} />,
    },
    {
      id: "decision",
      header: t("Decision"),
      enableSorting: false,
      cell: ({ row }) => {
        const r = row.original;
        if (r.status === "pending") return <span className="ada-faint">{t("Awaiting the DAR")}</span>;
        return (
          <div className="ada-stack ada-stack--tight">
            <span className="ada-faint">
              {r.decided_at ? formatDateTime(r.decided_at) : ""}
              {r.decided_by ? ` · ${r.decided_by}` : ""}
            </span>
            {r.effective_date ? <span>{t("Effective date: {{date}}", { date: formatDate(r.effective_date) })}</span> : null}
          </div>
        );
      },
    },
  ];
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("Requests")}</CardTitle>
      </CardHeader>
      <CardContent>
        {data.requests.length ? (
          <div className="ada-table-wrap">
            <DataTable columns={columns} data={data.requests} rowKey={(r) => String(r.id)} />
          </div>
        ) : (
          <p className="ada-faint">{t("No request in {{year}}.", { year: data.year })}</p>
        )}
      </CardContent>
    </Card>
  );
}

// ------------------------------------------------------------------------------------ page

export function PersonFollowUpPage() {
  const { t } = useTranslation();
  const { id: rawId = "" } = useParams();
  const id = Number(rawId);
  const [params, setParams] = useSearchParams();
  const year = readYear(params.get("year"));
  const followUp = usePersonFollowUp(id, year);
  // keepPreviousData spans every key change: never show another person's year under this URL.
  const data = followUp.data?.person.id === id ? followUp.data : undefined;
  useDocumentTitle(data?.person.name ?? t("Follow-up"));

  const setYear = (value: string) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("year", value);
        return next;
      },
      { replace: true },
    );

  if (!Number.isInteger(id) || id <= 0 || (followUp.isError && followUp.error instanceof ApiError && followUp.error.status === 404)) {
    return (
      <div className="ada-page">
        <EmptyBlock icon="user" title={t("Person not found")} />
      </div>
    );
  }
  if (followUp.isError && followUp.error instanceof ApiError && followUp.error.status === 403) {
    return (
      <div className="ada-page">
        <EmptyBlock
          icon="lock"
          title={t("Restricted follow-up")}
          description={t("A person's follow-up is open to that person, their coach and the DAR.")}
        />
      </div>
    );
  }
  if (followUp.isError && !data) {
    return (
      <div className="ada-page">
        <ErrorBlock error={followUp.error} onRetry={() => void followUp.refetch()} />
      </div>
    );
  }
  if (!data) {
    return (
      <div className="ada-page">
        <LoadingBlock lines={8} label={t("Loading the follow-up…")} />
      </div>
    );
  }

  const { person, coach } = data;
  return (
    <Page
      kicker={t("Follow-up")}
      title={person.name}
      description={person.title || undefined}
      meta={
        <div className="ada-cluster">
          {!person.active ? <Tag>{t("Departed")}</Tag> : null}
          <span className="ada-faint">
            {coach ? t("Coach: {{name}}", { name: coach.name }) : t("No coach in Boond")}
          </span>
          {person.email ? <span className="ada-faint">{person.email}</span> : null}
        </div>
      }
      actions={
        <SelectField
          label={t("Year")}
          value={String(year)}
          onChange={setYear}
          options={yearOptions(year).map((y) => ({ value: String(y), label: String(y) }))}
        />
      }
    >
      {/* While another year loads, the previous one stays on screen, dimmed. */}
      <div className={`ada-stack ada-stack--loose${data.year !== year ? " ada-fu-stale" : ""}`} aria-busy={data.year !== year}>
        <AllocationCard data={data} />
        <AssignmentsCard data={data} />
        <ReportHistory data={data} />
        <RequestsCard data={data} />
      </div>
    </Page>
  );
}
